// src/pages/Dashboard.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Image,
  Input,
  Upload,
  Button,
  Typography,
  Space,
  message,
} from "antd";
import { UploadOutlined, ReloadOutlined } from "@ant-design/icons";
import { API_BASE_URL } from "../api/api";

const { Title } = Typography;

// ✅ Set this to your actual PHP endpoint name
// e.g. `${API_BASE_URL}hotels` or `${API_BASE_URL}hotelMedia`
const HOTELS_ENDPOINT = `${API_BASE_URL}hotelImageUpload`;

const Dashboard = () => {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [search, setSearch] = useState("");

  const fetchHotels = async () => {
    setLoading(true);
    try {
      const res = await fetch(HOTELS_ENDPOINT, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();

      if (!json?.success) {
        throw new Error(json?.error || "Failed to fetch hotels");
      }
      // Expecting array of rows with: hotelCode, hotelName, country, city, cover_img_aws_url
      setHotels(
        Array.isArray(json.data) ? json.data : json.data ? [json.data] : []
      );
    } catch (err) {
      console.error(err);
      message.error("Failed to load hotels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalized = (v) => (v ?? "").toString().toLowerCase().trim();
  const filteredHotels = useMemo(() => {
    const q = normalized(search);
    if (!q) return hotels;
    return hotels.filter((h) => {
      const id = normalized(h.hotelCode);
      const name = normalized(h.hotelName);
      const city = normalized(h.city);
      const country = normalized(h.country);
      return (
        id.includes(q) ||
        name.includes(q) ||
        city.includes(q) ||
        country.includes(q)
      );
    });
  }, [search, hotels]);

  const updateRowCover = (hotelCode, url) => {
    setHotels((prev) =>
      prev.map((h) =>
        String(h.hotelCode) === String(hotelCode)
          ? { ...h, cover_img_aws_url: url }
          : h
      )
    );
  };

  const handleUpload = (record) => ({
    beforeUpload: () => false, // prevent antd from auto-uploading
    showUploadList: false,
    accept: "image/*",
    maxCount: 1,
    onChange: async (info) => {
      const file = info.file;
      if (!(file instanceof File)) {
        message.error("Invalid file selected");
        return;
      }
      const formData = new FormData();
      formData.append("hotelCode", record.hotelCode);
      formData.append("cover_img", file); // <-- matches PHP field name

      const hide = message.loading("Uploading image...", 0);
      try {
        const res = await fetch(HOTELS_ENDPOINT, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
        const json = await res.json();
        if (!json?.success) {
          throw new Error(json?.message || json?.error || "Upload failed");
        }
        updateRowCover(record.hotelCode, json.cover_img_aws_url);
        message.success("Cover image updated");
      } catch (err) {
        console.error(err);
        message.error("Upload failed");
      } finally {
        hide();
      }
    },
  });

  const columns = [
    {
      title: "Hotel ID",
      dataIndex: "hotelCode",
      key: "hotelCode",
      sorter: (a, b) => String(a.hotelCode).localeCompare(String(b.hotelCode)),
      width: 120,
    },
    {
      title: "Hotel Name",
      dataIndex: "hotelName",
      key: "hotelName",
      sorter: (a, b) =>
        String(a.hotelName || "").localeCompare(String(b.hotelName || "")),
      ellipsis: true,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      sorter: (a, b) =>
        String(a.city || "").localeCompare(String(b.city || "")),
      width: 160,
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      sorter: (a, b) =>
        String(a.country || "").localeCompare(String(b.country || "")),
      width: 160,
    },
    {
      title: "Cover Image",
      key: "cover_img_aws_url",
      dataIndex: "cover_img_aws_url",
      width: 180,
      render: (url) =>
        url ? (
          <Image
            src={url}
            alt="Cover"
            width={120}
            height={80}
            style={{ objectFit: "cover", borderRadius: 6, cursor: "zoom-in" }}
            // 👇 AntD's built-in lightbox; click to view large
            preview={{
              src: url, // full-size source
              mask: <span>Click to preview</span>,
            }}
          />
        ) : (
          <span style={{ color: "#999" }}>No Image</span>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space>
          <Upload {...handleUpload(record)}>
            <Button icon={<UploadOutlined />}>Upload Image</Button>
          </Upload>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2} style={{ marginBottom: 16 }}>
        Hotels
      </Title>

      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search by ID, name, city, or country"
          allowClear
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 360 }}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchHotels}>
          Refresh
        </Button>
      </Space>

      <Table
        rowKey={(r) => String(r.hotelCode)}
        loading={loading}
        columns={columns}
        dataSource={filteredHotels}
        pagination={{ pageSize: 10, showSizeChanger: true }}
      />
    </div>
  );
};

export default Dashboard;
